// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetCartsApiParameter } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartsDataView } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCarts from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCarts";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { CustomAppState, xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { xIsAuthenticated } from "@west/App/Store/Selectors/SessionSelectors";
import SavedCartsSummaryCard, { SavedCartsSummaryCardStyles } from "@west/App/Widgets/Common/SavedCartsSummaryCard";
import Tooltip, { TooltipPresentationProps } from "@west/MobiusOverrides/Tooltip";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import { FC, useEffect } from "react";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    showTitle = "showTitle",
    showViewAll = "showViewAll",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.showTitle]: boolean;
        [fields.showViewAll]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        savedCartsDataView: getCartsDataView(state, recentSavedOrdersParameter),
        savedOrderListPageLink: getPageLinkByPageType(state, "SavedOrderListPage"),
        canViewSavedOrders: xIsAuthenticated(state),
        tabs: state.xTabs,
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        isHomepage: getCurrentPage(state).type === "HomePage",
    };
};

const mapDispatchToProps = {
    loadCarts,
    updateZone: (zone: string, enabled: boolean) => ({
        type: "xTabs/EnableZone",
        data: {
            zone,
            isEnabled: enabled, // we need to show this tab if we are in tab context
        },
    }),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentSavedCartStyles {
    cardList?: CardListStyles;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    viewAllGridItem?: GridItemProps;
    viewAllLink?: LinkPresentationProps;
    savedOrderSummaryCardStyles?: SavedCartsSummaryCardStyles;
    subtotalText?: TypographyPresentationProps;
    subtotalTooltip?: TooltipPresentationProps;
}

export const recentSavedCartStyles: RecentSavedCartStyles = {
    noOrdersFoundGridItem: { width: 12 },
};

const recentSavedOrdersParameter: GetCartsApiParameter = {
    page: 1,
    sort: "orderDate DESC",
    status: "Saved",
};

const styles = recentSavedCartStyles;
const RecentSavedCarts: FC<Props> = ({
    savedOrderListPageLink,
    savedCartsDataView,
    loadCarts,
    canViewSavedOrders,
    fields: { showTitle, showViewAll, numberOfRecords },
    updateZone,
    zone,
    tabs,
    xGoogleAnalytics,
    isHomepage,
}) => {
    useEffect(() => {
        if (!savedCartsDataView.value && !savedCartsDataView.isLoading) {
            recentSavedOrdersParameter.pageSize = numberOfRecords;
            loadCarts({ apiParameter: recentSavedOrdersParameter });
        }
    }, []);

    useEffect(() => {
        if (canViewSavedOrders && savedCartsDataView?.value && !tabs[zone]) {
            updateZone(zone, true);
        } else if ((!canViewSavedOrders || !savedCartsDataView?.value) && tabs[zone]) {
            updateZone(zone, false);
        }
    }, [savedCartsDataView?.value]);

    const savedOrderHistoryUrl = savedOrderListPageLink ? savedOrderListPageLink.url : undefined;

    if (!canViewSavedOrders || !savedCartsDataView.value) {
        return null;
    }

    const gaSavedCartsClickHandler = () => {
        if (xGoogleAnalytics.hpRecentSavedCartsEvent && isHomepage) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.hpRecentSavedCartsEventName,
                click_text: translate("View All Saved Carts"),
                destination: savedOrderHistoryUrl,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    return (
        <StyledWrapper>
            <CardList extendedStyles={styles.cardList} data-test-selector="widget_SavedCarts">
                {showTitle && (
                    <CardListHeading heading={translate("Recent Saved Carts")} viewAllUrl={savedOrderHistoryUrl} />
                )}
                {savedCartsDataView.value.length === 0 ? (
                    <GridItem className="no-saved-carts-found-grid-item" {...styles.noOrdersFoundGridItem}>
                        <Typography
                            className="no-saved-carts-found-text"
                            {...styles.noOrdersFoundText}
                            data-test-selector="recentSavedCarts_NoOrdersFound"
                        >
                            {translate("No saved carts found")}
                        </Typography>
                    </GridItem>
                ) : (
                    <>
                        <GridItem width={12}>
                            <DataTable {...styles.dataTable}>
                                <DataTableHead {...styles.dataTableHead}>
                                    <DataTableHeader {...styles.dataTableHeaders}>{translate("Date")}</DataTableHeader>
                                    <DataTableHeader {...styles.dataTableHeaders}>{translate("Name")}</DataTableHeader>
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        {translate("Ship To / Pick Up")}
                                    </DataTableHeader>
                                    <DataTableHeader {...styles.dataTableHeaders}>
                                        <Typography {...styles.subtotalText}>{translate("Cart Subtotal")} </Typography>

                                        <Tooltip
                                            {...styles.subtotalTooltip}
                                            text={siteMessage("Custom_SavedCart_SubtotalMessage").toString()}
                                        />
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBody}>
                                    {savedCartsDataView.value?.map(
                                        order =>
                                            order !== null && (
                                                <SavedCartsSummaryCard
                                                    key={order.id}
                                                    cart={order}
                                                    extendedStyles={styles.savedOrderSummaryCardStyles}
                                                />
                                            ),
                                    )}
                                </DataTableBody>
                            </DataTable>
                        </GridItem>
                        {showViewAll && (
                            <GridItem {...styles.viewAllGridItem}>
                                <Link
                                    href={savedOrderHistoryUrl}
                                    {...styles.viewAllLink}
                                    aria-label={translate("View All Saved Carts")}
                                    onClick={gaSavedCartsClickHandler}
                                >
                                    {translate("View All Saved Carts")}
                                </Link>
                            </GridItem>
                        )}
                    </>
                )}
            </CardList>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentSavedCarts),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
            {
                name: fields.showTitle,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                variant: "toggle",
            },
            {
                name: fields.showViewAll,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                variant: "toggle",
            },
        ],
    },
};

export default widgetModule;
